import { Grid, Hidden } from "@material-ui/core";
import produce from "immer";
import { noop, pick } from "lodash";
import moment from "moment";
import { FunctionComponent, useCallback, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { QueryObserverBaseResult, UseQueryResult } from "react-query";
import { compose } from "redux";
import styled, { css } from "styled-components";
import { Checkbox } from "../../../components/forms/checkbox";
import { EditableField } from "../../../components/forms/field";
import { Select } from "../../../components/forms/select";
import { TextField } from "../../../components/forms/text";
import { KycIcon } from "../../../components/icons/status";
import { Flex, WrapGridCell } from "../../../components/layout";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { GlobalOverlayContext } from "../../../components/overlay/context";
import { ProcessingStatus } from "../../../components/processing-status";
import { Widget } from "../../../components/widget";
import { ProductionBlockchains } from "../../../store/chain/models/internal";
import { countries } from "../../../store/common/models";
import { useParticipation } from "../../../store/participations/queries";
import { ParticipationsDTO } from "../../../store/participations/models/dto";
import { ParticipationsService } from "../../../store/participations/service";
import { device } from "../../../styles/media";
import { mapObject, useYupValidationResolver } from "../../../utils/utils";

import { Resource } from "../../../components/loader/resource";
import { Adornment } from "./buttons";
import { RemoveParticipationModal } from "./remove-modal";

const PostDTO = ParticipationsDTO.Requests.SetSingle;
type PostDTOType = ParticipationsDTO.Requests.SetSingle.T;

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;
`;

const StyledSelect = styled(Select)`
  width: 15em;
`;

const secondField = css`
  max-width: 100%;
  display: flex;

  @media ${device.laptopL} {
    display: inline-flex;
    margin-left: 1em;
  }
`;

const StyledTextField = styled(TextField)`
  ${secondField};
  width: 30em;
`;

const PledgeAmountTextField = styled(TextField)`
  ${secondField};
  width: 15em;
`;

const Label = styled.span`
  font-weight: 500;
`;

export const InnerParticipationDetailForm: FunctionComponent<{
  id: string;
  item: ParticipationsDTO.Responses.GetSingle.T;
  refetch: QueryObserverBaseResult<
    ParticipationsDTO.Responses.GetSingle.T,
    unknown
  >["refetch"];
}> = ({ item, id, refetch }) => {
  const fields = PostDTO.useFields();

  const resolver = compose(
    useYupValidationResolver(fields),
    (data: PostDTOType) =>
      produce(data, (d) => {
        if (d.pledge) {
          if (d.pledge.amount === null) delete d.pledge.amount;
          if (d.pledge.currency === null) delete d.pledge.currency;
          if (Object.keys(d.pledge).length < 1) delete d.pledge;
        }
        if (d.sourceWallet) {
          if (d.sourceWallet.chain === null) delete d.sourceWallet.chain;
          if (d.sourceWallet.address === null) delete d.sourceWallet.address;
          if (Object.keys(d.sourceWallet).length < 1) delete d.sourceWallet;
        }
        if (d.destinationWallet) {
          if (d.destinationWallet.chain === null)
            delete d.destinationWallet.chain;
          if (d.destinationWallet.address === null)
            delete d.destinationWallet.address;
          if (Object.keys(d.destinationWallet).length < 1)
            delete d.destinationWallet;
        }
      })
  );
  const [editing, setEditing] = useState<boolean>(false);
  const loader = useAppLoader();

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<PostDTOType>({
    resolver,
    defaultValues: pick(item, [
      "pledge",
      "sourceWallet",
      "destinationWallet",
      "stakingWallet",
    ]),
  });

  const { setOverlay } = useContext(GlobalOverlayContext);

  const onDeleteHandler = useCallback(() => {
    setOverlay({
      title: "Delete Participation",
      children: <RemoveParticipationModal id={id} />,
    });
  }, [setOverlay]);

  const {
    user: {
      emailAddress,
      firstName,
      middleName,
      lastName,
      nationality,
      applicationPin,
    },
    fraudCheckId,
    credentialsId,
    referrer,
    createdDate,
    kycStatus,
    editable,
    processingStatus,
    policies: {
      sekuritanceStatements,
      finalDeclaration,
      declarationStatements,
      whitepaperRead,
    },
    regulations,
  } = item;

  const onSubmit = (data: ParticipationsDTO.Requests.SetSingle.T) => {
    loader(
      ParticipationsService.updateParticipation(id, data)
        .then(() => setEditing(false))
        .then(() => refetch())
        .catch(noop)
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StyledWidget
        title="Participation Overview"
        adornment={
          editable ? (
            <Hidden smDown>
              <Adornment
                fraudCheckId={fraudCheckId}
                credentialsId={credentialsId}
                onDeleteHandler={onDeleteHandler}
                setEditing={(x) => {
                  setEditing(x);
                  if (!x) {
                    reset();
                  }
                }}
                editing={editing}
              />
            </Hidden>
          ) : null
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {editable ? (
              <Hidden mdUp>
                <Flex justify="center">
                  <Adornment
                    fraudCheckId={fraudCheckId}
                    credentialsId={credentialsId}
                    onDeleteHandler={onDeleteHandler}
                    setEditing={(x) => {
                      setEditing(x);
                      if (!x) {
                        reset();
                      }
                    }}
                    editing={editing}
                  />
                </Flex>
              </Hidden>
            ) : null}
          </Grid>
          <Grid item lg={6} xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>Name:</Label>
              </Grid>
              <Grid item xs={6}>
                {firstName} {middleName} {lastName}
              </Grid>
              <Grid item xs={6}>
                <Label>Email:</Label>
              </Grid>
              <Grid item xs={6}>
                {emailAddress}
              </Grid>
              <Grid item xs={6}>
                <Label>Nationality:</Label>
              </Grid>
              <Grid item xs={6}>
                {countries.enum[nationality as typeof countries.T]}
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={6} xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Label>KYC Status:</Label>
              </Grid>
              <Grid item xs={6}>
                {KycIcon(kycStatus, "kyc")}
              </Grid>
              <Grid item xs={6}>
                <Label>Processing Status:</Label>
              </Grid>
              <Grid item xs={6}>
                <ProcessingStatus processingStatus={processingStatus} />
              </Grid>
              <Grid item xs={6}>
                <Label>Date Submitted:</Label>
              </Grid>
              <Grid item xs={6}>
                {moment.utc(createdDate).local().format("yyyy-MM-DD HH:mm")}
              </Grid>
              <Grid item xs={6}>
                <Label>Referrer:</Label>
              </Grid>
              <Grid item xs={6}>
                {referrer}
              </Grid>
              <Grid item xs={6}>
                <Label>Application Pin:</Label>
              </Grid>
              <Grid item xs={6}>
                {applicationPin}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <Label>Pledged:</Label>
          </Grid>
          <Grid item xs={9}>
            <EditableField
              editing={editing}
              control={control}
              name="pledge.currency"
            >
              <TextField
                label="Pledge Currency"
                error={!!errors.pledge?.currency}
                helperText={errors.pledge?.currency?.message}
              />
            </EditableField>
            <EditableField
              editing={editing}
              control={control}
              name="pledge.amount"
            >
              <PledgeAmountTextField
                type="number"
                label="Pledge Amount"
                error={!!errors.pledge?.amount}
                helperText={errors.pledge?.amount?.message}
              />
            </EditableField>
          </Grid>
          <Grid item xs={3}>
            <Label>Source Wallet:</Label>
          </Grid>
          <WrapGridCell xs={9}>
            <EditableField
              editing={editing}
              control={control}
              name="sourceWallet.chain"
            >
              <StyledSelect
                label="Source Wallet Chain"
                error={!!errors.sourceWallet?.chain}
                options={mapObject(
                  ProductionBlockchains.enum,
                  (value, key) => ({
                    title: value,
                    value: key,
                  })
                )}
                helperText={errors.sourceWallet?.chain?.message}
              />
            </EditableField>

            <EditableField
              editing={editing}
              control={control}
              name="sourceWallet.address"
            >
              <StyledTextField
                type="text"
                label="Source Wallet Address"
                error={!!errors.sourceWallet?.address}
                helperText={errors.sourceWallet?.address?.message}
              />
            </EditableField>
          </WrapGridCell>
          <Grid item xs={3}>
            <Label>Destination Wallet:</Label>
          </Grid>
          <WrapGridCell xs={9}>
            <EditableField
              editing={editing}
              control={control}
              name="destinationWallet.chain"
            >
              <StyledSelect
                label="Destination Wallet Chain"
                error={!!errors.destinationWallet?.chain}
                options={mapObject(
                  ProductionBlockchains.enum,
                  (value, key) => ({
                    title: value,
                    value: key,
                  })
                )}
                helperText={errors.destinationWallet?.chain?.message}
              />
            </EditableField>

            <EditableField
              editing={editing}
              control={control}
              name="destinationWallet.address"
            >
              <StyledTextField
                type="text"
                label="Destination Wallet Address"
                error={!!errors.destinationWallet?.address}
                helperText={errors.destinationWallet?.address?.message}
              />
            </EditableField>
          </WrapGridCell>
          <Grid item xs={3}>
            <Label>Rewards Wallet:</Label>
          </Grid>
          <WrapGridCell xs={9}>
            <EditableField
              editing={editing}
              control={control}
              name="stakingWallet.chain"
            >
              <StyledSelect
                label="Rewards Wallet Chain"
                error={!!errors.stakingWallet?.chain}
                options={mapObject(
                  ProductionBlockchains.enum,
                  (value, key) => ({
                    title: value,
                    value: key,
                  })
                )}
                helperText={errors.stakingWallet?.chain?.message}
              />
            </EditableField>

            <EditableField
              editing={editing}
              control={control}
              name="stakingWallet.address"
            >
              <StyledTextField
                type="text"
                label="Rewards Wallet Address"
                error={!!errors.stakingWallet?.address}
                helperText={errors.stakingWallet?.address?.message}
              />
            </EditableField>
          </WrapGridCell>
        </Grid>
      </StyledWidget>
      <StyledWidget title="Participant Details">
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Label>Final Declaration</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={finalDeclaration} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Pep</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={regulations?.pep} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>US Person</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={regulations?.usPerson} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Reside Sanctioned Country</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox
              checked={regulations?.resideSanctionedCountry}
              disabled
            />
          </Grid>
          <Grid item xs={3}>
            <Label>Declaration Statements</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={declarationStatements} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Whitepaper Read</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={whitepaperRead} disabled />
          </Grid>
          <Grid item xs={3}>
            <Label>Sekuritance Statements</Label>
          </Grid>
          <Grid item xs={3}>
            <StyledCheckbox checked={sekuritanceStatements} disabled />
          </Grid>
        </Grid>
      </StyledWidget>
    </form>
  );
};

export const ParticipationDetailForm: FunctionComponent<{ id: string }> = ({
  id,
}) => (
  <Resource r={useParticipation(id)}>
    {useCallback(
      (
        data: ParticipationsDTO.Responses.GetSingle.T,
        {
          refetch,
        }: UseQueryResult<ParticipationsDTO.Responses.GetSingle.T, unknown>
      ) => (
        <InnerParticipationDetailForm item={data} id={id} refetch={refetch} />
      ),
      [id]
    )}
  </Resource>
);
